/**
 * Apex Puzzle — The Tutorial Gate (Liminal Atrium).
 *
 * Single co-op puzzle for Level 0. Teaches all six base abilities:
 *   1. Basil stands on a pressure plate AND locks his Pneumatic Anchor (3)
 *      while on it — the plateAnchored latch requires both in the same frame
 *   2. Basil uses Turn-Table Touch on the dial (rhythm timing mini-game)
 *   3. Minna uses Aura Sight to reveal the hidden crystal receiver
 *   4. Minna uses Calming Resonance (Purr) to charge the crystal
 *   5. Dial + crystal both ready -> portal opens
 *   6. Both cats enter the portal -> level complete
 *
 * This module owns the plate, dial, crystal receiver, and exit portal,
 * plus all puzzle event wiring. The scene shell handles the lighting,
 * environment art, shadow zones, readability cues, and opening cutscene.
 *
 * The puzzle emits `LEVEL_COMPLETE` itself (rather than relying on the
 * scene shell) because Level 0 has no Elder encounter — portal entry is
 * both the puzzle solve and the level completion.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

import { PressurePlate } from "../../../entities/interactives/PressurePlate";
import { Dial } from "../../../entities/interactives/Dial";
import { CrystalReceiver } from "../../../entities/interactives/CrystalReceiver";
import { Portal } from "../../../entities/interactives/Portal";

import { GameEvents } from "../../../config/events";
import { TUTORIAL_STEP_CUES } from "../../../ui/tutorialCues";

import type { PuzzleContext, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

export type TutorialReadabilityStep = "plate" | "dial" | "crystal" | "portal" | "complete";

export interface TutorialStepFlags {
  /** Transient plate contact — telemetry only; does NOT advance the step. */
  platePressed: boolean;
  /** Latched: plate pressed AND Pneumatic Anchor active in the same frame. */
  plateAnchored: boolean;
  dialAligned: boolean;
  dialLocked: boolean;
  crystalCharged: boolean;
  portalOpen: boolean;
  levelComplete: boolean;
}

/**
 * Pure, monotonic step resolver: returns the FURTHEST milestone reached.
 *
 * Evaluated latest → earliest so that once a later flag is set the prompt
 * never falls back to an earlier step — even if an earlier flag (e.g.
 * platePressed on a hold-plate) reverts to false.
 *
 * Order of milestones: complete > portal > crystal(+dial done) > dial > plate
 * Crystal only counts as "done" once the dial is also locked, matching the
 * win condition in checkPuzzleComplete(). The plate step only counts as
 * taught once the plateAnchored latch fires (plate held + Anchor active) —
 * a bare walk-over (platePressed) never advances the prompt.
 */
export function deriveDisplayStep(flags: TutorialStepFlags): TutorialReadabilityStep {
  if (flags.levelComplete) return "complete";
  if (flags.portalOpen) return "portal";
  if (flags.crystalCharged && flags.dialLocked) {
    // Dial + crystal done. The win gate also requires the anchor beat:
    // point at the portal only when it latched, otherwise back to the
    // plate. That is completion of a never-taught step, not a regression
    // of a taught one — plateAnchored is a one-way latch, so a taught
    // plate step can never re-show.
    return flags.plateAnchored ? "portal" : "plate";
  }
  if (flags.dialLocked) return "crystal";
  if (flags.dialAligned) return "crystal"; // aligned but not locked yet → still on crystal step
  if (flags.plateAnchored) return "dial";
  return "plate";
}

export interface TutorialReadabilitySnapshot {
  step: TutorialReadabilityStep;
  platePressed: boolean;
  plateAnchored: boolean;
  dialAligned: boolean;
  dialLocked: boolean;
  crystalCharged: boolean;
  portalOpen: boolean;
  pressurePlate: PressurePlate;
  dial: Dial;
  crystalReceiver: CrystalReceiver;
  portal: Portal;
}

export class TutorialGatePuzzle implements PuzzleModule {
  readonly id = "tutorialGate";
  readonly displayName = "The Co-op Gate";

  private ctx!: PuzzleContext;

  // Public so the scene shell can wire readability cues to these meshes.
  pressurePlate!: PressurePlate;
  dial!: Dial;
  crystalReceiver!: CrystalReceiver;
  portal!: Portal;

  // Puzzle state
  private platePressed = false;
  // Same-frame latch: plate.pressed && pneumaticAnchor.isActive, checked in
  // update(). Event-only latching off PRESSURE_PLATE_ACTIVATED would reopen
  // the walk-past hole — the plate event fires transiently during spawn
  // settle (cats land on platform3 where the plate sits).
  private plateAnchored = false;
  private dialAligned = false;
  private dialLocked = false;
  private crystalCharged = false;
  private portalOpen = false;
  private levelComplete = false;

  // Turn-Table Touch target tracking
  private turnTableActive = false;

  // Tutorial teaching — fire a toast the first time the player tries
  // abilities the main puzzle chain doesn't mechanically require so
  // Level 0 still introduces all six base abilities.
  private taughtShadowSlip = false;
  private taughtOverclockedLeap = false;
  private taughtSharedCamera = false;
  // One-shot "press 3" nudge when Basil touches the plate without anchoring.
  private taughtAnchorNudge = false;

  // Throttle for the "portal is sealed" hint so it doesn't spam every
  // frame the player stands in front of the closed portal.
  private lastSealedHintAt = -Infinity;

  // Each step's "how" is a short cue chip (ui/tutorialCues.ts) pinned
  // above the thing the step is about — see showStepPrompt(). It replaced
  // full-sentence prompts in a wrapped panel over the middle of the
  // screen, which covered the cats and the platforms they jump to.

  // Short "what to do now" objectives for the always-on objective line.
  // These are the minimal action label — no control details, just the goal.
  // null means the step is done; showStepPrompt() emits HUD_SET_OBJECTIVE
  // with empty text to clear the line immediately.
  private static readonly STEP_OBJECTIVES: Record<TutorialReadabilityStep, string | null> = {
    plate: "Anchor Basil on the pressure plate",
    dial: "Spin the brass dial",
    crystal: "Reveal and charge the crystal",
    portal: "Walk both cats into the portal",
    complete: null,
  };

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // Register with the level handle so the Babylon MCP bridge (and any
    // future dev overlay) can read this puzzle's readability snapshot via
    // `scene.metadata.level.getPuzzleState()`. We expose a lean snapshot —
    // primitives only — because the full readability snapshot carries live
    // InteractiveObject refs whose mesh graphs are not serializable.
    //
    // `getInternals` is the dev-only counterpart that returns the live
    // refs (dial, pressurePlate, crystalReceiver, portal) so QA can call
    // methods directly (e.g. `dial.spin()`) through the bridge's
    // `get_puzzle_internals` verb. This replaces the old level-specific
    // `scene.metadata.liminalDebug` hook that every level would have
    // needed to reinvent.
    const level = scene.metadata?.level as
      | {
          setPuzzle?: (puzzle: {
            getState: () => unknown;
            getInternals?: () => unknown;
          }) => void;
        }
      | undefined;
    level?.setPuzzle?.({
      getState: () => ({
        puzzleId: this.id,
        step: this.getCurrentReadabilityStep(),
        platePressed: this.platePressed,
        plateAnchored: this.plateAnchored,
        dialAligned: this.dialAligned,
        dialLocked: this.dialLocked,
        crystalCharged: this.crystalCharged,
        portalOpen: this.portalOpen,
        levelComplete: this.levelComplete,
      }),
      getInternals: () => ({
        puzzleId: this.id,
        step: this.getCurrentReadabilityStep(),
        platePressed: this.platePressed,
        plateAnchored: this.plateAnchored,
        dialAligned: this.dialAligned,
        dialLocked: this.dialLocked,
        crystalCharged: this.crystalCharged,
        portalOpen: this.portalOpen,
        levelComplete: this.levelComplete,
        pressurePlate: this.pressurePlate,
        dial: this.dial,
        crystalReceiver: this.crystalReceiver,
        portal: this.portal,
      }),
    });

    // ── Interactives ──
    this.pressurePlate = new PressurePlate(scene, {
      key: "tutorialPlate",
      position: new Vector3(4, 2.2, 0),
      label: "Pressure Plate",
    });

    this.dial = new Dial(scene, {
      key: "tutorialDial",
      position: new Vector3(8, 3.9, 0),
      totalNotches: 8,
      targetNotch: 5,
      label: "Brass Dial",
    });

    this.crystalReceiver = new CrystalReceiver(scene, {
      key: "tutorialCrystal",
      // plat1 raised to y=2.2 (top at 2.4), so crystal lifts to 2.7 to
      // sit 0.3 above the platform surface like the original 1.7→2.0 gap.
      position: new Vector3(-5, 2.7, 0),
      hidden: true,
      label: "Crystal Receiver",
    });

    this.portal = new Portal(scene, {
      key: "tutorialPortal",
      position: new Vector3(-8, 3.7, 0),
      label: "Portal to Redwood Scrapyard",
    });

    // ── Register AuraSight interactives ──
    // Registered in plate -> dial -> crystal -> portal order so the scene
    // shell can chain connections through `allInteractives`.
    ctx.registerInteractive({
      mesh: this.pressurePlate.rootMesh,
      highlightTarget: this.pressurePlate.getHighlightTarget(),
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.dial.rootMesh,
      highlightTarget: this.dial.getHighlightTarget(),
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.crystalReceiver.rootMesh,
      highlightTarget: this.crystalReceiver.getHighlightTarget(),
      hidden: true,
      state: "hidden",
      onReveal: () => this.crystalReceiver.reveal(),
      onConceal: () => this.crystalReceiver.conceal(),
    });
    ctx.registerInteractive({
      mesh: this.portal.rootMesh,
      highlightTarget: this.portal.getHighlightTarget(),
      hidden: false,
      state: "revealed",
    });

    // ── Event subscriptions ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PRESSURE_PLATE_ACTIVATED, (key) => {
        if (key === "tutorialPlate") {
          this.platePressed = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
            puzzleId: this.id,
            step: "platePressed",
          });
          // Bare plate contact does NOT advance the tutorial anymore —
          // the taught mechanic is "anchor on the plate" and the prompt
          // says "press 3", so a walk-over advancing the step was
          // teaching theater. Instead, nudge the player once toward the
          // anchor. The real advance happens in update() when the
          // plateAnchored latch fires.
          if (!this.plateAnchored && !this.taughtAnchorNudge) {
            this.taughtAnchorNudge = true;
            ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
              message: "He's on the plate — press 3 to lock Basil's brace.",
            });
          }
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PRESSURE_PLATE_DEACTIVATED, (key) => {
        if (key === "tutorialPlate") {
          this.platePressed = false;
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.DIAL_ALIGNED, (key) => {
        if (key === "tutorialDial") {
          this.dialAligned = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
            puzzleId: this.id,
            step: "dialAligned",
          });
          // Auto-lock on alignment: Dial.onInteract('pneumaticAnchor') is
          // the only caller of dial.lock() and nothing in the gameplay
          // pipeline ever invokes it, so the dialLocked gate used to be
          // dead wiring. Player prompts teach plate → dial → crystal →
          // portal with no "lock" step, so alignment acts as the implicit
          // latch.
          this.dial.lock();
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.DIAL_TURNED, (key) => {
        if (key !== "tutorialDial") return;
        if (!this.dial.aligned) {
          this.dialAligned = false;
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.DIAL_LOCKED, (key) => {
        if (key === "tutorialDial") {
          this.dialLocked = true;
          this.showStepPrompt("crystal");
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.DIAL_UNLOCKED, (key) => {
        if (key === "tutorialDial") {
          this.dialLocked = false;
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CRYSTAL_ACTIVATED, (key) => {
        if (key === "tutorialCrystal") {
          this.crystalCharged = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
            puzzleId: this.id,
            step: "crystalCharged",
          });
          this.checkPuzzleComplete();
          this.showStepPrompt(this.getCurrentReadabilityStep());
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PORTAL_ENTERED, () => {
        if (this.levelComplete) return;
        this.levelComplete = true;
        ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
        ctx.gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "LiminalAtrium" });
        ctx.gameEvents.emit(GameEvents.HUD_HIDE_PROMPT);
      }),
    );

    // Initial prompt — fire when the opening cutscene ends rather than
    // inside setup(), because setup runs before the cutscene starts and
    // the cutscene's own dialogue overwrites the HUD prompt panel. We
    // unsubscribe after the first fire so a later cutscene (e.g. an
    // Elder encounter) doesn't reset the prompt to "plate".
    let initialPromptFired = false;
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CUTSCENE_ENDED, () => {
        if (initialPromptFired) return;
        initialPromptFired = true;
        if (this.getCurrentReadabilityStep() === "plate") {
          this.showStepPrompt("plate");
        }
        // Movement keys, taught in play instead of a wall of cutscene
        // text: the chip stays until both cats have jumped (in solo that
        // also means the player has found Tab).
        ctx.gameEvents.emit(GameEvents.HUD_SET_CUE, { slot: "controls", cue: "controls" });
      }),
    );
    const jumped = new Set<string>();
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PLAYER_JUMPED, (cat) => {
        if (jumped.size === 2) return;
        jumped.add(cat);
        if (jumped.size === 2) {
          ctx.gameEvents.emit(GameEvents.HUD_SET_CUE, { slot: "controls", cue: null });
        }
      }),
    );

    // Mode-refresh: when the player presses F2 mid-level, re-emit the
    // CURRENT sticky prompt with copy that names the newly active controls
    // (Tab↔split-keyboard). Runs after the monotonic resolver so the
    // refresh can never push a regressed step — `getCurrentReadabilityStep`
    // always returns the furthest milestone already reached.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.INPUT_MODE_CHANGED, () => {
        const step = this.getCurrentReadabilityStep();
        if (step !== "complete") {
          this.showStepPrompt(step);
        }
      }),
    );

    // Turn-Table Touch rhythm hit -> spin the dial
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
        this.dial.spin();
      }),
    );

    // Tutorial teaching toasts for the two base abilities the main puzzle
    // chain doesn't mechanically require. The first time the player
    // actually tries each ability in Level 0 we surface a "nice — you
    // learned X" toast so Level 0 still introduces all six base abilities.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.SHADOW_SLIP_ENTERED, () => {
        if (this.taughtShadowSlip) return;
        this.taughtShadowSlip = true;
        ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Shadow Slip: Minna vanishes into shadow. Predators won't see you while you're hidden.",
        });
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.OVERCLOCKED_LEAP_FIRED, () => {
        if (this.taughtOverclockedLeap) return;
        this.taughtOverclockedLeap = true;
        ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Overclocked Leap: Basil's harness hurls him skyward — use it to reach high platforms.",
        });
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PLAYER_SEPARATION_CLAMPED, () => {
        if (this.taughtSharedCamera || this.levelComplete) return;
        this.taughtSharedCamera = true;
        ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Stay together: the shared frame slows whichever cat tries to leave the other behind.",
        });
      }),
    );
  }

  update(dt: number, _elapsed: number): void {
    const { minna, basil, calmingResonance, basilAbilities } = this.ctx;

    // Pressure plate: check pressure from player meshes each frame.
    this.pressurePlate.checkPressure([minna.mesh, basil.mesh]);

    // Anchor latch — the taught plate mechanic. Requires BOTH conditions
    // in the same frame: the plate held down AND the Pneumatic Anchor
    // locked. Same-frame conjunction means neither a spawn-settle plate
    // transient nor anchoring off-plate can false-latch. Once latched it
    // never reverts (monotonic step derivation handles prompt display).
    if (
      !this.plateAnchored &&
      this.pressurePlate.pressed &&
      this.ctx.pneumaticAnchor.isActive
    ) {
      this.plateAnchored = true;
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: "plateAnchored",
      });
      this.checkPuzzleComplete();
      // Re-derive the sticky prompt from the monotonic resolver: shows
      // "dial" in the normal order, and "portal" when the anchor beat
      // was the LAST prerequisite (dial + crystal already done — the
      // portal just opened in checkPuzzleComplete above). Never rewinds
      // a later step.
      const step = this.getCurrentReadabilityStep();
      if (step !== "complete") {
        this.showStepPrompt(step);
      }
    }

    // Dial: auto-deactivate Turn-Table Touch when Basil wanders away.
    // Players kept losing the ring mid-hit without any feedback loop —
    // the toast closes the gap so it's clear *why* the ring vanished.
    // Skip the check entirely once the dial is locked: at that point
    // the dial stage is done, the ring should already be gone, and
    // firing the "drifted" toast while Basil is walking to the portal
    // reads as a confusing false-alarm.
    if (!this.dialLocked) {
      const basilDistToDial = Vector3.Distance(
        basil.mesh.position,
        this.dial.rootMesh.position,
      );
      if (basilDistToDial >= 3 && this.turnTableActive) {
        basilAbilities.deactivateAbility("turnTableTouch");
        this.turnTableActive = false;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Basil drifted too far — come back to the dial to keep spinning.",
        });
      }
    } else if (this.turnTableActive) {
      // Dial is locked — tear down any still-active Turn-Table Touch
      // silently so the rhythm ring doesn't linger over Basil as he
      // walks away.
      basilAbilities.deactivateAbility("turnTableTouch");
      this.turnTableActive = false;
    }

    // CrystalReceiver: charges when Minna's CalmingResonance is active
    // and she's within its radius.
    let isPurring = false;
    if (calmingResonance.isActive) {
      const crystalDist = Vector3.Distance(
        minna.mesh.position,
        this.crystalReceiver.rootMesh.position,
      );
      isPurring = crystalDist <= calmingResonance.getRadius();
    }
    this.crystalReceiver.updateCharge(dt, isPurring);

    // Portal entry check — only once the portal is open.
    if (this.portalOpen) {
      this.portal.checkEntry(minna.mesh.position, basil.mesh.position);
    } else {
      // Sealed-portal feedback: if either cat is approaching the closed
      // portal, surface a HUD hint explaining what's still missing
      // (otherwise the player just bumps into a dark arch with zero
      // feedback). 3.0u fires well before visual contact so QA found
      // readability was lagging the collision; 6s throttle keeps the
      // wider trigger from spamming while the player lingers.
      const portalPos = this.portal.rootMesh.position;
      const minnaNear = Vector3.Distance(minna.mesh.position, portalPos) < 3.0;
      const basilNear = Vector3.Distance(basil.mesh.position, portalPos) < 3.0;
      if (minnaNear || basilNear) {
        const now = _elapsed;
        if (now - this.lastSealedHintAt > 6) {
          this.lastSealedHintAt = now;
          const missing: string[] = [];
          if (!this.plateAnchored) missing.push("anchored plate (press 3)");
          if (!this.dialLocked) missing.push("dial");
          if (!this.crystalCharged) missing.push("crystal");
          const message = missing.length > 0
            ? `The portal is sealed. Still need: ${missing.join(", ")}.`
            : "The portal is sealing — give it a moment.";
          this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, { message });
        }
      }
    }
  }

  onAbilityKey(key: string, pressed: boolean, _released: boolean): boolean {
    // 1 = toggle Turn-Table Touch when Basil is near the dial.
    if (key === "1" && pressed) {
      const distToDial = Vector3.Distance(
        this.ctx.basil.mesh.position,
        this.dial.rootMesh.position,
      );
      if (distToDial < 3) {
        if (!this.turnTableActive) {
          // Route through the holder (mirrors the deactivation path) so
          // ABILITY_ACTIVATED fires for HUD/telemetry/banter; latch on
          // the actual result so turnTableActive cannot lie true.
          this.ctx.turnTableTouch.setTarget(
            this.dial.rootMesh.position,
            (_combo) => {},
          );
          this.turnTableActive =
            this.ctx.basilAbilities.activateAbility("turnTableTouch");
        } else {
          this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
          this.turnTableActive = false;
        }
      }
      return true;
    }

    // 2 = rhythm hit when Turn-Table Touch is active.
    if (key === "2" && pressed && this.turnTableActive) {
      this.ctx.turnTableTouch.attemptHit();
      return true;
    }

    return false;
  }

  getState(): PuzzleState {
    if (this.levelComplete) return { solved: true, progress: 1 };
    // Four sub-steps: plate anchored, dial aligned, dial locked, crystal
    // charged. plateAnchored replaces the old transient platePressed so
    // progress math stays 4-step but reflects the real win condition.
    // Portal entry is the final solve signal. Report [0, 0.99] until
    // the portal is entered so the apex contract is satisfied.
    const steps = [
      this.plateAnchored,
      this.dialAligned,
      this.dialLocked,
      this.crystalCharged,
    ].filter(Boolean).length;
    const portalBonus = this.portalOpen ? 0.1 : 0;
    return {
      solved: false,
      progress: Math.min(0.99, steps / 4 + portalBonus),
    };
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "pressurePlate", position: { x: 4, y: 2.2 }, radius: 1.0, allowedCat: "either", label: "Pressure plate (activated by either cat's weight)" },
        { id: "dial",          position: { x: 8, y: 3.9 }, radius: 1.2, allowedCat: "basil", label: "Co-op dial (Basil's Turn-Table Touch target)" },
        { id: "crystal",       position: { x: -5, y: 2.7 }, radius: 1.2, allowedCat: "either", label: "Crystal receiver (Minna charges via Purr; Basil may visit as leapfrog anchor)" },
        { id: "portal",        position: { x: -8, y: 3.7 }, radius: 1.5, allowedCat: "either", label: "Exit portal" },
      ],
      traversals: [
        // TT-Touch rhythm cadence on the tutorial dial. Default ATRIUM
        // tuning is forgiving (markerSpeed=1.4, halfWidth=1.0 →
        // 39% per-press hit rate). 50 presses at 200ms reaches the
        // ATRIUM dial target with margin.
        //
        // The L0-tutorialGate plan inlines this cadence directly via
        // pressKey, but the recipe is here so a `traverse` invocation
        // produces the same result — useful for a future tutorial-
        // hint cutscene that wants to demo the rhythm beat without
        // open-coding the press loop.
        {
          id: "tutorial_dial_rhythm",
          kind: "ability",
          cat: "basil",
          from: "dial",
          to: "dial",
          successWindowMs: 12_000,
          recipe: [
            // Activate TT-Touch on the dial.
            { key: "1", holdMs: 80 },
            { waitMs: 200 },
            // Cadence — 50 presses at 200ms.
            ...Array.from({ length: 50 }, () => [
              { key: "2", holdMs: 60 },
              { waitMs: 200 },
            ]).flat(),
          ],
        },
      ],
    };
  }

  dispose(): void {
    this.ctx?.gameEvents.emit(GameEvents.HUD_HIDE_PROMPT);
    this.pressurePlate.dispose();
    this.dial.dispose();
    this.crystalReceiver.dispose();
    this.portal.dispose();
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     platePressed?: boolean,   // flip transient platePressed flag (telemetry only)
   *     plateAnchored?: boolean,  // latch the anchor-on-plate win-gate flag
   *     dialAligned?: boolean,    // mark dial aligned + locked
   *     crystalCharged?: boolean, // mark crystal charged (opens portal if deps met)
   *     portalOpen?: boolean,     // force-open portal (sets all prior flags true)
   *     solved?: boolean,         // full solve — emits LEVEL_COMPLETE + PUZZLE_SOLVED
   *   }
   *
   * Use `portalOpen: true` to stage the "walk into portal" step for a
   * trace testing the final LEVEL_COMPLETE handshake. Unrecognized keys
   * are silently ignored.
   */
  seed(state: Record<string, unknown>): void {
    if (state.platePressed === true) {
      this.platePressed = true;
    }
    if (state.plateAnchored === true && !this.plateAnchored) {
      this.plateAnchored = true;
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: "plateAnchored",
      });
      this.checkPuzzleComplete();
    }
    if (state.dialAligned === true && !this.dialAligned) {
      this.dialAligned = true;
      this.dialLocked = true;
      // Lock the dial so downstream proximity deactivation mirrors normal play.
      if (!this.dial.locked) this.dial.lock();
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: "dialAligned",
      });
    }
    if (state.crystalCharged === true && !this.crystalCharged) {
      this.crystalCharged = true;
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
        puzzleId: this.id,
        step: "crystalCharged",
      });
      this.checkPuzzleComplete();
    }
    if (state.portalOpen === true) {
      // Force all prerequisite flags true so checkPuzzleComplete succeeds.
      this.platePressed = true;
      this.plateAnchored = true;
      this.dialAligned = true;
      this.dialLocked = true;
      this.crystalCharged = true;
      this.checkPuzzleComplete();
    }
    if (state.solved === true && !this.levelComplete) {
      // Replicate the PORTAL_ENTERED path: flip every sub-flag, open the
      // portal, then emit PUZZLE_SOLVED + LEVEL_COMPLETE.
      this.platePressed = true;
      this.plateAnchored = true;
      this.dialAligned = true;
      this.dialLocked = true;
      this.crystalCharged = true;
      this.checkPuzzleComplete();
      this.levelComplete = true;
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
      this.ctx.gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "LiminalAtrium" });
      this.ctx.gameEvents.emit(GameEvents.HUD_HIDE_PROMPT);
    }
  }

  /**
   * QA force-solve hook — `window.__qa.puzzle.forceSolve("tutorialGate")`
   * routes through `BaseLevel.forceSolvePuzzle`, which clamps the wrapped
   * `getState()` to solved and emits PUZZLE_SOLVED, then calls this hook.
   * Without it the internal win-gate flags stayed false, so `update()`
   * kept firing the sealed-portal hint and the portal never opened.
   *
   * Replays the solved seed's world-state path via `seed({ portalOpen:
   * true })` — latches platePressed/plateAnchored/dialAligned/dialLocked/
   * crystalCharged and opens the portal — so the two QA fast-forwards
   * cannot drift apart. Deliberately does NOT set `levelComplete` or emit
   * LEVEL_COMPLETE: checkpoint restore re-applies `forceSolvePuzzle` on
   * load, and completing the level here would exit L0 the moment a
   * restored save re-entered it. Portal entry stays the LEVEL_COMPLETE
   * handshake, exactly as after `seed({ portalOpen: true })`.
   */
  onForceSolve(): void {
    this.seed({ portalOpen: true });
    // Refresh the sticky prompt so the HUD stops teaching a step the
    // force-solve just skipped ("portal" here; "complete" hides it).
    this.showStepPrompt(this.getCurrentReadabilityStep());
  }

  /**
   * Point the HUD at a tutorial step: the always-on objective line says
   * what to do (not gated by the hints toggle) and a cue chip above the
   * step's object says who does it and which keys (HUD_SET_CUE; keys are
   * resolved by the HUD for the live input mode and active cat, so an F2
   * or Tab switch needs no re-emit). "complete" clears both.
   */
  private showStepPrompt(step: TutorialReadabilityStep): void {
    const objective = TutorialGatePuzzle.STEP_OBJECTIVES[step];
    this.ctx.gameEvents.emit(GameEvents.HUD_SET_OBJECTIVE, { text: objective ?? "" });
    // Clear any legacy bottom prompt a previous step or cutscene left.
    this.ctx.gameEvents.emit(GameEvents.HUD_HIDE_PROMPT);

    const cue = TUTORIAL_STEP_CUES[step];
    const target = this.stepTarget(step);
    if (!cue || !target) {
      this.ctx.gameEvents.emit(GameEvents.HUD_SET_CUE, { slot: "objective", cue: null });
      return;
    }
    this.ctx.gameEvents.emit(GameEvents.HUD_SET_CUE, {
      slot: "objective",
      cue,
      anchor: { x: target.x, y: target.y + 1.2, z: target.z },
    });
  }

  /** World position of the object a step is about. */
  private stepTarget(step: TutorialReadabilityStep): Vector3 | null {
    switch (step) {
      case "plate": return this.pressurePlate.rootMesh.position;
      case "dial": return this.dial.rootMesh.position;
      case "crystal": return this.crystalReceiver.rootMesh.position;
      case "portal": return this.portal.rootMesh.position;
      default: return null;
    }
  }

  // ─────────────────────────────────────────────────────────────────

  private checkPuzzleComplete(): void {
    // Win gate includes the plateAnchored latch — the taught mechanic
    // ("press 3 to anchor on the plate") is now mechanically required,
    // matching the level contract's requiredAbilities: pneumaticAnchor.
    if (this.plateAnchored && this.dialAligned && this.dialLocked && this.crystalCharged) {
      if (!this.portalOpen) {
        this.portalOpen = true;
        this.portal.open();
        this.crystalReceiver.solveCrystal();
        this.dial.solve();
        this.pressurePlate.solve();
        // Progress pulse so the scene shell can light up the final
        // readability cues regardless of which sub-step fired last.
        this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, {
          puzzleId: this.id,
          step: "portalOpened",
        });
      }
    }
  }

  getReadabilitySnapshot(): TutorialReadabilitySnapshot {
    return {
      step: this.getCurrentReadabilityStep(),
      platePressed: this.platePressed,
      plateAnchored: this.plateAnchored,
      dialAligned: this.dialAligned,
      dialLocked: this.dialLocked,
      crystalCharged: this.crystalCharged,
      portalOpen: this.portalOpen,
      pressurePlate: this.pressurePlate,
      dial: this.dial,
      crystalReceiver: this.crystalReceiver,
      portal: this.portal,
    };
  }

  debugSetReadabilityStage(stage: TutorialReadabilityStep): void {
    this.platePressed = stage !== "plate";
    this.plateAnchored = stage !== "plate";
    this.dialAligned = stage === "crystal" || stage === "portal" || stage === "complete";
    this.dialLocked = this.dialAligned;
    this.crystalCharged = stage === "portal" || stage === "complete";

    if (stage === "portal" || stage === "complete") {
      if (!this.portalOpen) {
        this.portalOpen = true;
        this.portal.open();
      }
    }
  }

  private getCurrentReadabilityStep(): TutorialReadabilityStep {
    return deriveDisplayStep({
      platePressed: this.platePressed,
      plateAnchored: this.plateAnchored,
      dialAligned: this.dialAligned,
      dialLocked: this.dialLocked,
      crystalCharged: this.crystalCharged,
      portalOpen: this.portalOpen,
      levelComplete: this.levelComplete,
    });
  }
}
